// What we may tell someone after a submission that did not obviously succeed.
//
// ⚠️ "Failed" is a CLAIM, and most of the time we are not entitled to make it.
// A request that got no response may have been received, processed and
// committed — the answer is what went missing, not the work. Telling that
// person "failed, try again" invites them to do it twice.
//
// For a payroll run, a disbursement or a loan approval, doing it twice is not
// an inconvenience. An unknown payment state is IN FLIGHT, never failed, and
// the correct next step is to find out, not to repeat.

export type SubmissionOutcome =
  /** The server answered and refused. It definitely did not take effect. */
  | 'rejected'
  /** We do not know. No response, or an answer that does not say. */
  | 'uncertain';

export interface SubmissionContext {
  /**
   * True when repeating this could move money, or create something the person
   * cannot easily undo: payroll, disbursements, payments, approvals.
   *
   * ⚠️ Default is TRUE at the call site in doubt. Treating a consequential
   * action as ordinary is the expensive mistake; the reverse only costs a
   * slightly more cautious sentence.
   */
  consequential?: boolean;
}

/**
 * ⚠️ 4xx vs 5xx is the whole distinction and it is not cosmetic.
 *
 * A 4xx is the server saying "no": validation, permission, conflict. It was
 * reached, it decided, and it did not act. That is safe to call rejected.
 *
 * A 5xx is the server falling over. It was reached — so it may have committed
 * the work before it fell. "Internal server error" is not evidence that
 * nothing happened, and treating it as such is how a payment gets made twice.
 *
 * No status at all (network error, timeout, abort) is the same uncertainty
 * with less information.
 */
export const classifySubmission = (status?: number | null): SubmissionOutcome => {
  if (status === undefined || status === null || status === 0) return 'uncertain';
  if (status >= 400 && status < 500) return 'rejected';
  return 'uncertain';
};

export type SubmissionAction =
  /** Safe to offer a retry button. */
  | 'retry'
  /** Do NOT offer a retry. Send them to check what actually happened. */
  | 'check';

export interface SubmissionAdvice {
  outcome: SubmissionOutcome;
  action: SubmissionAction;
  /** What to say. Never asserts failure unless failure is known. */
  message: string;
}

export const submissionAdvice = (
  status: number | null | undefined,
  ctx: SubmissionContext = {},
): SubmissionAdvice => {
  const outcome = classifySubmission(status);

  if (outcome === 'rejected') {
    return {
      outcome,
      action: 'retry',
      message: 'This was not accepted, so nothing has changed. You can correct it and try again.',
    };
  }

  // ⚠️ Consequential and uncertain: the one case where a retry button is the
  // wrong control to put on screen, however much the person wants one.
  if (ctx.consequential) {
    return {
      outcome,
      action: 'check',
      message:
        'We did not get a reply, so this may or may not have gone through. Check its status before trying again — repeating it could duplicate it.',
    };
  }

  return {
    outcome,
    action: 'retry',
    message: 'We did not get a reply, so this may not have gone through. Check before retrying if you are unsure.',
  };
};

/**
 * Whether the form may be cleared.
 *
 * ⚠️ Only on a SUCCESS we actually observed. Clearing someone's work because a
 * request did not come back loses the work AND the evidence, and is the
 * difference between "try again" and "type it all again".
 */
export const mayClearEdits = (observedSuccess: boolean): boolean => observedSuccess;
